import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import identityService from "../Services/identityService";
import passkeyService from "../Services/passkeyService";
import tokenStore from "../Utils/tokenStore";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session from stored token on mount
  useEffect(() => {
    if (tokenStore.isTokenExists()) {
      const userData = tokenStore.getUser();
      if (userData?.Email || userData?.UserGuid || userData?.UserId) {
        setUser(userData);
      }
    }
    setIsLoading(false);
  }, []);

  const persistAndSetUser = useCallback((jwt, rememberMe) => {
    tokenStore.saveToken({ ...jwt, rememberMe: !!rememberMe });
    const userData = tokenStore.getUser();
    setUser(userData);
    return userData;
  }, []);

  // Email + password login. Returns { requiresMfa, userId } or throws.
  const login = useCallback(
    async (email, password, rememberMe) => {
      const result = await identityService.loginWithEmail({
        email,
        password,
        rememberMe,
      });
      if (result?.requiresMfa) {
        return { requiresMfa: true, userId: result.userId, mfaMethod: "email" };
      }
      if (result?.token) {
        persistAndSetUser(result, rememberMe);
        return { success: true };
      }
      throw new Error("Login failed: unexpected server response.");
    },
    [persistAndSetUser],
  );

  // Phone login — server sends OTP. Returns { userId }.
  const loginWithPhone = useCallback(async (phoneNumber) => {
    const result = await identityService.loginWithPhone(phoneNumber);
    if (result?.userId) return { userId: result.userId };
    throw new Error("Phone login failed: unexpected server response.");
  }, []);

  // Verify email MFA code after loginWithEmail returned requiresMfa.
  const verifyEmailMfa = useCallback(
    async (userId, code, rememberMe) => {
      const result = await identityService.verifyLoginMfa(userId, code);
      if (result?.token) {
        persistAndSetUser(result, rememberMe);
        return { success: true };
      }
      throw new Error("MFA verification failed.");
    },
    [persistAndSetUser],
  );

  // Verify phone OTP after loginWithPhone.
  const verifyPhoneMfa = useCallback(
    async (userId, code) => {
      const result = await identityService.verifyPhoneLoginMfa(userId, code);
      if (result?.token) {
        persistAndSetUser(result, false);
        return { success: true };
      }
      throw new Error("Phone verification failed.");
    },
    [persistAndSetUser],
  );

  // Resend email MFA code.
  const resendEmailMfa = useCallback(async (userId) => {
    return identityService.resendLoginMfa(userId);
  }, []);

  // WebAuthn passkey authentication.
  const loginWithPasskey = useCallback(
    async (email) => {
      const result = await passkeyService.authenticateWithEmail(email);
      if (result?.token) {
        persistAndSetUser(result, false);
        return { success: true };
      }
      throw new Error("Passkey authentication failed.");
    },
    [persistAndSetUser],
  );

  // Redirect browser to identity server's Microsoft OAuth entry point.
  const loginWithMicrosoft = useCallback(async () => {
    // Ensure Service Worker is registered before SSO callback returns
    // (clearing site data may have wiped it)
    if ("serviceWorker" in navigator) {
      try {
        await navigator.serviceWorker.register("/serviceWorker.js");
        await navigator.serviceWorker.ready;
      } catch (err) {
        // Continue even if SW registration fails; app still works
      }
    }

    const callbackUrl = `${window.location.origin}/auth/callback`;
    const url = identityService.getExternalProviderLoginUrl(
      "Microsoft",
      callbackUrl,
    );
    window.location.href = url;
  }, []);

  // Called by the /auth/callback page after Microsoft redirects back with tokens.
  const handleMicrosoftCallback = useCallback(
    (accessToken, refreshToken) => {
      persistAndSetUser(
        { token: accessToken, refreshToken: refreshToken || "" },
        false,
      );
      return tokenStore.getUser();
    },
    [persistAndSetUser],
  );

  // Clear all session data.
  const logout = useCallback(() => {
    tokenStore.removeToken();
    localStorage.removeItem("rememberMe");
    setUser(null);
  }, []);

  // Forgot password — step 1: request reset email.
  const requestPasswordReset = useCallback(async (email) => {
    return identityService.requestPasswordReset({ email });
  }, []);

  // Forgot password — step 2: verify the code.
  const verifyPasswordResetCode = useCallback(async (email, code) => {
    return identityService.verifyPasswordResetCode({ email, code });
  }, []);

  // Forgot password — step 3: set new password.
  const completePasswordReset = useCallback(
    async (email, token, newPassword, confirmPassword) => {
      return identityService.completePasswordReset({
        email,
        token,
        newPassword,
        confirmPassword,
      });
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        loginWithPhone,
        verifyEmailMfa,
        verifyPhoneMfa,
        resendEmailMfa,
        loginWithPasskey,
        loginWithMicrosoft,
        handleMicrosoftCallback,
        logout,
        requestPasswordReset,
        verifyPasswordResetCode,
        completePasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export default AuthContext;
